import {
  Attachment01,
  CornerUpLeft,
  File04,
  Microphone01,
  Send01,
  XClose,
} from "@untitled-ui/icons-react";
import axios from "axios";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import Button from "@/components/forms/Button";
import EmojiPicker from "@/components/forms/EmojiPicker";
import Textarea from "@/components/forms/Textarea";
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from "@/components/ui/hover-card";
import { useMediaQuery } from "@/hooks/useMediaQuery";
import { getApiError } from "@/lib/api";
import { cn } from "@/lib/utils";
import { type Media } from "@/types";
import { useUploadFile } from "@/user/services";
import { notify } from "@/utils";
import { logError } from "@/utils/logger";

import {
  MAX_VOICE_BYTES,
  useVoiceRecorder,
} from "../../hooks/useVoiceRecorder";
import { type Message, type MessengerMessageType } from "../../types";
import VoiceRecorderBar from "../voice/VoiceRecorderBar";
import FileSendModal from "./FileSendModal";

interface MessageInputProps {
  onSendMessage: (
    content: string,
    file?: Media,
    replyTo?: number,
    type?: MessengerMessageType,
  ) => void;
  onTyping: () => void;
  replyingTo?: Message<true> | null;
  onCancelReply?: () => void;
  isLoading?: boolean;
  variant?: "fullscreen" | "compact";
  className?: string;
}

export default function MessageInput({
  onSendMessage,
  onTyping,
  replyingTo,
  onCancelReply,
  isLoading = false,
  variant = "compact",
  className,
}: MessageInputProps) {
  const { t } = useTranslation("messenger.user");
  const [content, setContent] = useState("");
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [fileSendModalFile, setFileSendModalFile] = useState<File | null>(null);
  const [fileSendModalOpen, setFileSendModalOpen] = useState(false);
  const [isMultiline, setIsMultiline] = useState(false);
  const singleLineHeightRef = useRef<number | null>(null);
  const voiceRecorder = useVoiceRecorder();
  const { mutateAsync: uploadVoice, isPending: isUploadingVoice } =
    useUploadFile("audio");
  const isDesktop = useMediaQuery("(min-width: 640px)");

  const isFullscreen = variant === "fullscreen";
  const isMobile = !isDesktop;
  const isVoiceSession = voiceRecorder.status !== "idle";

  useEffect(() => {
    if (!replyingTo) return;
    const timer = window.setTimeout(() => textareaRef.current?.focus(), 100);
    return () => window.clearTimeout(timer);
  }, [replyingTo]);

  const handleSendMessage = () => {
    if (content.trim() === "") return;

    onSendMessage(content.trim(), undefined, replyingTo?.id);
    setContent("");
    onCancelReply?.();
    requestAnimationFrame(() => {
      textareaRef.current?.focus();
    });
  };

  const handleFileSend = (caption: string, media: Media) => {
    onSendMessage(caption.trim(), media, replyingTo?.id);
    onCancelReply?.();
    setFileSendModalOpen(false);
    setFileSendModalFile(null);
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selected = e.target.files?.[0];
    if (!selected) return;
    setFileSendModalFile(selected);
    setFileSendModalOpen(true);
    e.target.value = "";
  };

  const handleEmojiSelect = (emoji: string) => {
    setContent((prev) => prev + emoji);
  };

  const handleStartRecording = async () => {
    try {
      await voiceRecorder.start();
    } catch (error) {
      logError(error, { context: "voice_recording_start" });
      const denied =
        error instanceof DOMException &&
        (error.name === "NotAllowedError" || error.name === "SecurityError");
      notify.error(
        t(denied ? "Microphone access denied" : "Microphone is unavailable"),
      );
    }
  };

  const handleSendVoice = async () => {
    try {
      const file = await voiceRecorder.stop();
      if (file.size > MAX_VOICE_BYTES) {
        notify.error(t("Voice message is too large"));
        voiceRecorder.reset();
        return;
      }
      const media = await uploadVoice(file);
      onSendMessage("", media, replyingTo?.id, "voice");
      onCancelReply?.();
      voiceRecorder.reset();
    } catch (error) {
      logError(error, { context: "voice_send" });
      if (axios.isAxiosError(error) && error.response?.status === 422) {
        const message = getApiError(error);
        if (message) notify.error(message);
      }
    }
  };

  const hasContent = content.trim() !== "";
  const isSendingVoice = isLoading || isUploadingVoice;

  const iconPositionClass = isMultiline
    ? "bottom-0 flex items-end"
    : "inset-y-0 flex items-center";

  const rowAlignClass =
    isMultiline || replyingTo ? "items-end" : "items-center";

  const messageField = (
    <div className="relative">
      <div className={cn("absolute start-0 z-10 p-1", iconPositionClass)}>
        <EmojiPicker
          onChange={handleEmojiSelect}
          keepOpen
          buttonClassName="hover:bg-transparent"
          iconClassName="size-4 text-gray-400"
        />
      </div>
      <Textarea
        ref={textareaRef}
        placeholder={t("Message")}
        className={cn(
          "max-h-23 w-full ltr:pr-10 ltr:pl-10 rtl:pr-10 rtl:pl-10",
          (isFullscreen || isMobile || replyingTo) && "border-0 shadow-none",
          (isFullscreen || isMobile || replyingTo) &&
            "focus:border-0 focus:shadow-none focus:ring-0 focus:outline-none",
        )}
        value={content}
        onChange={(e) => setContent(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter" && !e.shiftKey) {
            e.preventDefault();
            handleSendMessage();
            return;
          }
          onTyping();
        }}
        visibleLine={1}
        autoHeight
        onAutoHeight={(height) => {
          if (singleLineHeightRef.current === null) {
            singleLineHeightRef.current = height;
          }
          setIsMultiline(height > singleLineHeightRef.current + 2);
        }}
      />
      <div
        className={cn(
          "pointer-events-auto absolute end-0 z-20 p-1",
          iconPositionClass,
        )}
      >
        <HoverCard openDelay={100} closeDelay={100}>
          <HoverCardTrigger asChild>
            <Button
              color="tertiary-gray"
              square
              size="lg"
              className="hover:bg-transparent"
            >
              <Attachment01 className="size-4 text-gray-400" />
            </Button>
          </HoverCardTrigger>
          <HoverCardContent
            side="top"
            align="end"
            className="w-auto min-w-max bg-white p-1 shadow-md"
          >
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              className="flex min-w-32 items-center gap-2 rounded-lg px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
            >
              <File04 className="size-4 text-gray-500" />
              {t("File")}
            </button>
          </HoverCardContent>
        </HoverCard>
      </div>
    </div>
  );

  const sendOrMicButton = hasContent ? (
    <Button
      color="primary"
      square
      size="lg"
      className="shrink-0"
      isLoading={isLoading}
      onClick={handleSendMessage}
    >
      <Send01 className="size-5 rtl:rotate-y-180" />
    </Button>
  ) : (
    <Button
      color={
        isMobile ? "primary" : isFullscreen ? "secondary-gray" : "tertiary-gray"
      }
      square
      size="lg"
      className="shrink-0"
      onClick={handleStartRecording}
    >
      <Microphone01
        className={cn(
          "size-5",
          isMobile
            ? "text-white"
            : isFullscreen
              ? "text-gray-700"
              : "text-gray-600",
        )}
      />
    </Button>
  );

  const recorderBar = (
    <VoiceRecorderBar
      recorder={voiceRecorder}
      stacked={isMobile || !isFullscreen}
      onSend={handleSendVoice}
      isSending={isSendingVoice}
    />
  );

  const replyPreview = replyingTo ? (
    <div className="flex items-center gap-2">
      <CornerUpLeft className="text-primary-700 size-5 shrink-0 rtl:-scale-x-100" />
      <div className="bg-primary-50 border-primary-500 min-w-0 flex-1 rounded-md border-s-[3px] py-1 ps-2">
        <div className="text-primary-700 truncate text-sm font-semibold">
          {replyingTo.user.first_name} {replyingTo.user.last_name}
        </div>
        <div className="text-primary-600 truncate text-sm">
          {replyingTo.type === "voice"
            ? t("Voice message")
            : replyingTo.file?.file_name || replyingTo.content || ""}
        </div>
      </div>
      <button
        type="button"
        onClick={onCancelReply}
        className="shrink-0 cursor-pointer"
      >
        <XClose className="text-primary-700 size-5" />
      </button>
    </div>
  ) : null;

  const fileInput = (
    <input
      ref={fileInputRef}
      type="file"
      className="hidden"
      onChange={handleFileInputChange}
    />
  );

  const composer = (
    <div
      className={cn(
        isMobile
          ? cn(
              "flex gap-1 rounded-[10px] border border-gray-300 bg-white py-1 pe-1 shadow-xs",
              "focus-within:border-primary-300 focus-within:ring-primary-100 focus-within:ring-4",
              isMultiline ? "items-end" : "items-center",
            )
          : cn("flex flex-nowrap gap-3", rowAlignClass),
      )}
    >
      <div
        className={cn(
          "min-w-0 flex-1",
          !isMobile && "relative isolate",
          isFullscreen && !isMobile && "flex flex-col",
        )}
      >
        {isFullscreen && !isMobile ? (
          <div
            className={cn(
              "rounded-lg border border-gray-300 bg-white shadow-xs",
              "focus-within:border-primary-300 focus-within:ring-primary-100 focus-within:ring-4",
            )}
          >
            {replyPreview && (
              <div className="px-3.5 pt-2.5 pb-2">{replyPreview}</div>
            )}
            {messageField}
          </div>
        ) : (
          messageField
        )}
      </div>
      {sendOrMicButton}
      {fileInput}
    </div>
  );

  const fileSendModal = (
    <FileSendModal
      open={fileSendModalOpen}
      file={fileSendModalFile}
      onClose={() => {
        setFileSendModalOpen(false);
        setFileSendModalFile(null);
      }}
      onSend={handleFileSend}
    />
  );

  if (isMobile) {
    return (
      <>
        <div
          className={cn(
            "relative shrink-0",
            isVoiceSession
              ? "border-t border-gray-200 bg-white"
              : "bg-linear-to-b from-white/0 to-white",
            className,
          )}
        >
          {replyPreview && <div className="px-3 pt-3">{replyPreview}</div>}
          <div className={cn("px-3 pb-4", replyPreview ? "pt-2" : "pt-3")}>
            {isVoiceSession ? recorderBar : composer}
          </div>
        </div>
        {fileSendModal}
      </>
    );
  }

  if (isFullscreen) {
    return (
      <>
        <div className={cn("relative shrink-0", className)}>
          <div className="px-20 pt-2 pb-4">
            {isVoiceSession ? (
              <div className="flex flex-col gap-2">
                {replyPreview}
                {recorderBar}
              </div>
            ) : (
              composer
            )}
          </div>
          <div
            aria-hidden
            className="via-primary-400/60 pointer-events-none absolute right-0 bottom-0 left-0 h-1 bg-linear-to-r from-transparent to-transparent"
          />
        </div>
        {fileSendModal}
      </>
    );
  }

  return (
    <>
      <div className={cn("shrink-0", className)}>
        {replyPreview && (
          <div className="border-t border-gray-200 px-3 py-2">
            {replyPreview}
          </div>
        )}
        <div
          className={cn(
            "px-3",
            replyPreview ? "pb-4" : "border-t border-gray-200 py-4",
          )}
        >
          {isVoiceSession ? recorderBar : composer}
        </div>
      </div>
      {fileSendModal}
    </>
  );
}
